import React, { useEffect, useState } from "react";
import {
  MenuItem,
  Typography,
  Checkbox,
  FormControlLabel,
  Box,
  Paper,
  Button,
  Grid,
  CircularProgress,
  Select,
  IconButton,
  Tooltip
} from "@material-ui/core";
import { Container } from "react-bootstrap";
import { creditCardTypes, getPaymentPreferences, getPaymentPreferencesList, savePaymentPreferences } from "../../store/actions/userOnboarding";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { isSameArray } from "../../config/utils";
import {withStyles } from '@material-ui/core/styles';
import InputBase from '@material-ui/core/InputBase';
import OtpInput from "../common/OtpInput";
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import { v4 as uuidv4 } from 'uuid';
import { useSelector } from "react-redux";

const BootstrapInput = withStyles((theme) => ({
  root: {
    'label + &': {
      marginTop: theme.spacing(3),
    },
  },
  input: {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #ced4da',
    fontSize: 16,
    padding: '10px 26px 10px 12px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    // Use the system font instead of the default Roboto font.
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    '&:focus': {
      borderRadius: 4,
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
    },
  },
}))(InputBase);

const PaymentSetup = (props) => {
  const {isViewUserRequest, data, activeStep, handleBack, handleNext, isApprovedSeller} = props;
  const [paymentPrefs, setPaymentPrefs] = useState([]);
  const [loader, setLoader] = useState(false);
  const [cardTypes, setCardTypes] = useState([]);
  const [savedPrefs, setSavedPrefs] = useState([]);
  const {accountDetails} = useSelector((state) => state.userOnboarding);
  const defaultPreference = 6; // Assuming 6 is the default preference ID to be selected for new users
  const [creditCardList, setCreditCardList] = useState([
    { id: uuidv4(), cardTypeId: 1, cardType: 'Visa', last4Digits: '' },
  ]);

  useEffect(() => {
    getPreferencesList();
    fetchCardTypes();
  }, []);


  const fetchCardTypes = async () => {
    try {
      const res = await creditCardTypes();
      if (res?.data?.Status === 200) {
        setCardTypes(res.data.EntityList);
      } else {
        toast.error("Failed to fetch credit card types. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred while fetching credit card types. Please try again.");
    }
  }

  const handleAddNewCard = () => {
    const newCard = { id: uuidv4(), cardTypeId: 0, cardType: '', last4Digits: '' };
    setCreditCardList(prev => [...prev, newCard]);
  };

  const handleDeleteCard = (id) => {
    setCreditCardList(prev => prev.filter(card => card.id !== id));
  }

  const getPreferencesList = async () => {
    setLoader(true);
    let paymentRes = await getPaymentPreferencesList(isApprovedSeller);
    if (paymentRes?.data) {
      let prefs = paymentRes.data.EntityList.map(item => ({
        ...item,
        isChecked: false
      }));
      setPaymentPrefs(prefs);
      getPreferences();
    }
  };

  const getPreferences = async () => {
    let paymentRes = await getPaymentPreferences(isViewUserRequest ? data.Id : null, isApprovedSeller);
    if (paymentRes?.data?.Status === 200) {
      setSavedPrefs(paymentRes.data.Entity.paymentModeIds);
      let savedData = paymentRes.data.Entity.paymentModeIds;
      setCreditCardList(paymentRes.data.Entity.creditCardDetails?.length > 0 ? paymentRes.data.Entity.creditCardDetails.map(card => ({
        id: uuidv4(),
        cardTypeId: card.creditCardTypeId,
        cardType: cardTypes.find(type => type.id === card.creditCardTypeId)?.name || '',
        last4Digits: card.lastFourDigits
      })) : [{ id: uuidv4(), cardTypeId: 1, cardType: 'Visa', last4Digits: '' }]);
      
      setPaymentPrefs(prev => prev.map(item => ({ ...item, isChecked: savedData.includes(item.id) })));
    } else {
      setPaymentPrefs(prev => prev.map(item => ({ ...item, isChecked: item.id === defaultPreference })));
    }
    setLoader(false);
  };

  const saveForm = async () => {
    let selectedPrefs = paymentPrefs.filter(item => item.isChecked).map(item => item.id);

    const isCardSelected = paymentPrefs.some(item => item.modeCode === 'CARD' && item.isChecked);
    if (isCardSelected && creditCardList.length === 0) {
      toast.error("Please add at least one credit card.");
      return;
    }

    if (isCardSelected && creditCardList.some(card => !card.cardTypeId)) {
      toast.error("Please select a card type for all credit cards.");
      return;
    }

    if (isCardSelected && creditCardList.some(card => card.last4Digits?.length !== 4)) {
      toast.error("Last four digits are required for all credit cards.");
      return;
    }

    if (isSameArray(selectedPrefs, savedPrefs) && !isCardSelected) {
      handleNext();
      return;
    }

    if (selectedPrefs.length === 0) {
      toast.error("Please select at least one payment preference.");
      return;
    }

    let payloadata = {
      paymentModeIds: selectedPrefs,
      onboardingId: isViewUserRequest ? data.Id : 0,
      creditCardDetails: isCardSelected ? creditCardList.map(card => ({
        creditCardTypeId: card.cardTypeId,
        lastFourDigits: card.last4Digits
      })) : []
    }
    setLoader(true);
    let res = await savePaymentPreferences(payloadata, isApprovedSeller);
    if (res?.data?.Status === 200) {
      toast.success("Payment preferences saved successfully.");
      setLoader(false);
      handleNext();
    } else {
      toast.error("Failed to save payment preferences. Please try again.");
      setLoader(false);
    }
  };

  const handleChange = (event, modeCode) => {
    const { checked } = event.target;
    setPaymentPrefs(prev => prev.map(item => item.modeCode === modeCode ? { ...item, isChecked: checked } : item));
  };

  const handleCardChange = (event, cardId) => {
    const { value } = event.target;
    let findIndex = creditCardList.findIndex(card => card.id === cardId);
    let updatedList = [...creditCardList];
    if (findIndex !== -1) {
      updatedList[findIndex].cardTypeId = value;
      const selectedType = cardTypes.find(type => type.id === value);
      updatedList[findIndex].cardType = selectedType?.name || '';
      setCreditCardList(updatedList);
    }
  };

  const handleChangeCreditNumber = (value, cardId) => {
    let findIndex = creditCardList.findIndex(card => card.id === cardId);
    let updatedList = [...creditCardList];
    if (findIndex !== -1) {
      updatedList[findIndex].last4Digits = value;
      setCreditCardList(updatedList);
    }
  };

  return (
    <Container fluid>
      <Paper className='onboardingFormWrapping fw-3' elevation={2} square>
        <Typography variant="h6">Select Payment Preference</Typography>
        {loader && (<div className="full-screen-spinner-overlay"><CircularProgress disableShrink /></div>)}
        <Box mt={4}>
            <Grid container spacing={2}>
                {paymentPrefs.map((item, index) => (
                  <Grid item xs={6} md={4} lg={3} key={item.id}>
                      <FormControlLabel
                        control={
                          <Checkbox
                              name={item.modeCode}
                              checked={item.isChecked}
                              onChange={(event) => handleChange(event, item.modeCode)}
                              disabled={isApprovedSeller && savedPrefs.includes(item.id)}
                              style={{ color: "#1b76af" }}
                          />
                        }
                        label={item.modeName}
                      />
                  </Grid>
                ))}
            </Grid>
        </Box>
      </Paper>
      
      {paymentPrefs.some(item => item.modeCode === 'CARD' && item.isChecked) && (
        <Paper className='onboardingFormWrapping fw-3 mt-3' elevation={2} square>
          <div className="agreement">
            <Box mb={2}>
              <Typography variant="body2">
                {isViewUserRequest && (
                  <>
                    I, <b>{data?.FullName}</b>{data?.CompanyName ? <> representing <b>{data.CompanyName}</b></> : ''}, authorize Minrak Inc (Cross Culture Mall - this is what will appear on your credit card statement) to charge my bill directly to the credit card/s listed below.
                  </>
                )}
                {!isViewUserRequest && (
                  <>
                    I, <b>{accountDetails?.firstName} {accountDetails?.lastName}</b>{accountDetails?.companyName ? <> representing <b>{accountDetails.companyName}</b></> : ''}, authorize Minrak Inc (Cross Culture Mall - this is what will appear on your credit card statement) to charge my bill directly to the credit card/s listed below.
                  </>
                )}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" mb={3}>
              <Button 
                variant="contained"
                size="small" 
                color="primary"
                className="mr-2"
                disableElevation
                onClick={handleAddNewCard}
              >
                Add New Credit Card <AddIcon />
              </Button>
              <Tooltip title="We require your credit card information to securely process payments. Your details will be encrypted and stored safely, ensuring a smooth and secure transaction experience." arrow>
                <Typography className="link" variant="body2" style={{ cursor: 'pointer' }}>Why do we need this?</Typography>
              </Tooltip>
            </Box>
            {console.log("creditCardList", creditCardList)}
            {creditCardList.map((card, index) => (
              <Grid container spacing={2} key={card.id} className="mb-2">
                <Grid item xs={7}>
                  <Typography variant="subtitle1">Select Credit Card Type</Typography>
                  <Select
                    outlined
                    labelId="demo-customized-select-label"
                    id="demo-customized-select"
                    value={card.cardTypeId}
                    onChange={(event) => handleCardChange(event, card.id)}
                    input={<BootstrapInput />}
                    style={{width: '100%'}}
                  >
                    <MenuItem value={0}>Select</MenuItem>
                    {cardTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="subtitle1">Enter Last 4 Digits of Credit Card</Typography>
                  <OtpInput
                    value={card.last4Digits}
                    onChange={(value) => handleChangeCreditNumber(value, card.id)}
                    numInputs={4}
                    renderInput={(props) => <input {...props} />}
                  />
                </Grid>
                <Grid item xs={1} style={{ textAlign: 'center' }}>
                  {creditCardList.length > 1 && (
                    <div className="mt-4">
                      <IconButton color="primary" aria-label="Delete card" component="label" onClick={() => handleDeleteCard(card.id)}>
                          <Tooltip title="Delete card" arrow>
                              <DeleteIcon style={{color: '#ff0000'}} />
                          </Tooltip>
                      </IconButton>
                    </div>
                  )}
                </Grid>
              </Grid>
            ))}

          </div>
        </Paper>
      )}
      <div className='flexPayment mt-2'>
        {!isViewUserRequest ? <Typography component="em">* Please complete the form to finalize your onboarding process</Typography> : <Typography component="em"></Typography>}
        <div className="mt-3 alignItemsCenter">
          <Button disabled={activeStep === 0} onClick={handleBack} disableElevation>
              Back
          </Button>
          <Button
            disableElevation
            variant="contained"
            color="primary"
            onClick={saveForm}
            className='ml-2 cus-seconday-bg-btn btn'
          >
            Save & Next
          </Button>
        </div>
      </div>
    </Container>
  );
};

export default PaymentSetup;

PaymentSetup.propTypes = {
    isViewUserRequest: PropTypes.bool,
  isApprovedSeller: PropTypes.bool,
    data: PropTypes.object,
    activeStep: PropTypes.number,
    handleBack: PropTypes.func,
    handleNext: PropTypes.func,
}